import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";

const Amenities = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [amenitiesData, setAmenitiesData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedAmenityForDelete, setSelectedAmenityForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [newAmenity, setNewAmenity] = useState({
    amenitiesName: '',
    amenitiesAlias: '',
    status: 'Active',
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch amenities
  const fetchAmenities = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=amenities`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const amenities = Array.isArray(result.data)
        ? result.data.map((item) => ({
          id: item._id,
          amenitiesName: item.details?.amenitiesName || item.details?.name || 'Unknown',
          amenitiesAlias: item.details?.amenitiesAlias || item.details?.alias || '',
          status: item.status === true ? 'Active' : 'Inactive',
          rawStatus: item.status,
        }))
        : [];

      setAmenitiesData(amenities);
    } catch (err) {
      setApiError('Failed to load amenities.');
      setAmenitiesData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAmenities();
  }, []);

  // Filter logic
  const getFilteredAmenities = () => {
    return amenitiesData.filter((a) => {
      const matchesSearch =
        a.amenitiesName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (a.amenitiesAlias && a.amenitiesAlias.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredAmenities = getFilteredAmenities();

  // Pagination
  const paginatedAmenities = filteredAmenities.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newAmenity.amenitiesName.trim()) err.amenitiesName = 'Amenities Name is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return amenitiesData.some(
      (a) =>
        a.amenitiesName.toLowerCase() === newAmenity.amenitiesName.trim().toLowerCase() &&
        a.id !== editingAmenity?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingAmenity(null);
    setNewAmenity({
      amenitiesName: '',
      amenitiesAlias: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingAmenity(null);
    setNewAmenity({
      amenitiesName: '',
      amenitiesAlias: '',
      status: 'Active',
    });
    setErrors({});
  };

  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setNewAmenity({
      amenitiesName: amenity.amenitiesName,
      amenitiesAlias: amenity.amenitiesAlias || '',
      status: amenity.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (amenity) => {
    setSelectedAmenityForDelete(amenity);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAmenityForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails/${selectedAmenityForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      setAlertConfig({ title: 'Success', message: 'Amenity deleted successfully!' });
      setAlertOpen(true);
      fetchAmenities(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete amenity.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedAmenityForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ amenitiesName: 'Amenities name already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'amenities',
        details: {
          amenitiesName: newAmenity.amenitiesName.trim(),
          amenitiesAlias: newAmenity.amenitiesAlias.trim(),
        },
        status: newAmenity.status === 'Active',
      };

      let response;
      if (editingAmenity) {
        response = await fetch(`${API_BASE}/api/globalMasterDetails/${editingAmenity.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/api/globalMasterDetails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error('Save failed');

      setAlertConfig({
        title: 'Success',
        message: editingAmenity ? 'Amenity updated successfully!' : 'Amenity added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchAmenities(true);
    } catch (err) {
      setErrors({ amenitiesName: 'Failed to save amenity.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
    fetchAmenities(true);
  };

  const handleChange = (field, value) => {
    setNewAmenity((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Amenities Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchAmenities()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}>
              Add Amenity
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the amenity <strong>{selectedAmenityForDelete?.amenitiesName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchAmenities()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Amenities..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredAmenities.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} amenities matching your criteria.` : 'No amenities found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !amenitiesData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amenities Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amenities Alias</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAmenities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No amenities found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAmenities.map((amenity, index) => (
                    <TableRow key={amenity.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{amenity.amenitiesName}</TableCell>
                      <TableCell>{amenity.amenitiesAlias || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={amenity.status}
                          color={amenity.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleEdit(amenity)} disabled={isLoading}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteClick(amenity)} disabled={isLoading}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredAmenities.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAmenity ? 'Edit Amenity' : 'Add New Amenity'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amenities Name"
                  value={newAmenity.amenitiesName}
                  onChange={(e) => handleChange('amenitiesName', e.target.value)}
                  error={!!errors.amenitiesName}
                  helperText={errors.amenitiesName}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amenities Alias"
                  value={newAmenity.amenitiesAlias}
                  onChange={(e) => handleChange('amenitiesAlias', e.target.value)}
                  helperText="Enter alias code or identifier"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newAmenity.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingAmenity ? 'Update' : 'Add'} Amenity
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Amenities;

